import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SvgComponent } from '@components/svg.component';
import { CtaButton } from '@components/button-cta.component';
import {
  PasswordStrengthErrors,
  PasswordValidationService,
} from '@services/password-validation.service';
import { UpdatePasswordRequest } from '@models/update-password.model';
import { GestionLinker } from '@services/gestion.service';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '@services/auth.service';

@Component({
  selector: 'app-user-reset',
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule, SvgComponent, CtaButton],
  templateUrl: './user-reset-password.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserResetPasswordComponent {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly auth = inject(AuthService);
  private readonly userService = inject(GestionLinker);
  private readonly passwordValidation = inject(PasswordValidationService);
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(TranslateService);

  isSubmitting = signal(false);
  showOldPassword = signal(false);
  showNewPassword = signal(false);
  showConfirmPassword = signal(false);
  showTooltip = signal(false);
  isRotating = signal(false);

  userId = this.route.snapshot.paramMap.get('userId');

  readonly passwordForm: FormGroup = this.fb.group(
    {
      oldPassword: ['', [Validators.required]],
      newPassword: [
        '',
        [
          Validators.required,
          this.passwordValidation.passwordStrengthValidator(),
        ],
      ],
      confirmPassword: ['', [Validators.required]],
    },
    {
      validators: [
        this.passwordValidation.passwordMatchValidator(
          'newPassword',
          'confirmPassword'
        ),
        this.passwordValidation.passwordDifferenceValidator(
          'oldPassword',
          'newPassword'
        ),
      ],
    }
  );

  goToDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  togglePasswordVisibility(field: 'old' | 'new' | 'confirm'): void {
    switch (field) {
      case 'old':
        this.showOldPassword.update(v => !v);
        break;
      case 'new':
        this.showNewPassword.update(v => !v);
        break;
      case 'confirm':
        this.showConfirmPassword.update(v => !v);
        break;
    }
  }

  get oldPasswordControl() {
    return this.passwordForm.get('oldPassword')!;
  }

  get newPasswordControl() {
    return this.passwordForm.get('newPassword')!;
  }

  get confirmPasswordControl() {
    return this.passwordForm.get('confirmPassword')!;
  }

  // Méthode typée pour récupérer les erreurs de validation
  getPasswordErrors(fieldName: string): PasswordStrengthErrors | null {
    const control = this.passwordForm.get(fieldName);
    if (!control || !control.errors) return null;

    // Cast sécurisé vers PasswordStrengthErrors
    return control.errors as PasswordStrengthErrors;
  }

  // Méthode utilitaire pour vérifier une erreur spécifique
  hasPasswordError(
    fieldName: string,
    errorType: keyof PasswordStrengthErrors
  ): boolean {
    const errors = this.getPasswordErrors(fieldName);
    return errors ? !!errors[errorType] : false;
  }

  isFieldInvalid(fieldName: string): boolean {
    const control = this.passwordForm.get(fieldName);
    return !!(control && control.invalid && control.touched);
  }

  getFieldErrors(fieldName: string): ValidationErrors | null {
    return this.passwordForm.get(fieldName)?.errors || null;
  }

  getPasswordStrengthClass(index: number): string {
    return this.passwordValidation.getPasswordStrengthBarClass(
      this.newPasswordControl,
      index
    );
  }

  getPasswordStrengthText(): string {
    const result = this.passwordValidation.getPasswordValidationResult(
      this.newPasswordControl
    );
    return result.strengthLabel;
  }

  updatePassword(): void {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    if (!this.userId) {
      return;
    }

    this.isSubmitting.set(true);
    this.isRotating.set(true);

    const { oldPassword, newPassword } = this.passwordForm.value;

    this.userService
      .updatePassword(
        new UpdatePasswordRequest(Number(this.userId), oldPassword, newPassword)
      )
      .subscribe({
        next: response => {
          if (response.success) {
            this.isSubmitting.set(false);
            this.isRotating.set(false);

            this.toastr.success(this.translate.instant(response.message));
            setTimeout(() => {
              this.auth.logout();
              this.router.navigate(['/']);
            }, 3000);
          } else {
            this.isSubmitting.set(false);
            this.isRotating.set(false);

            this.toastr.error(this.translate.instant(response.message));
          }
        },
        error: error => {
          console.error('Erreur:', error);
          this.isSubmitting.set(false);
          this.isRotating.set(false);
        },
      });
  }
}
